import React from "react";

/**
 * PhosphorIcon — single icon family (Phosphor Regular style) rendered inline
 * as SVG so every icon inherits `color: currentColor` and shares one stroke
 * weight. No mixing libraries, no <img>, no white backgrounds.
 *
 * Usage: <PhosphorIcon name="ShieldCheck" size={24} />
 */

const PATHS = {
  // Compliance / docs
  FileText: (
    <>
      <path d="M200 224H56a8 8 0 0 1-8-8V40a8 8 0 0 1 8-8h96l56 56v128a8 8 0 0 1-8 8Z" />
      <path d="M152 32v56h56" />
      <line x1="96" y1="136" x2="160" y2="136" />
      <line x1="96" y1="168" x2="160" y2="168" />
    </>
  ),
  Receipt: (
    <>
      <path d="M48 216V40a8 8 0 0 1 8-8h144a8 8 0 0 1 8 8v176l-32-16-32 16-32-16-32 16-32-16Z" />
      <line x1="88" y1="96" x2="168" y2="96" />
      <line x1="88" y1="136" x2="168" y2="136" />
    </>
  ),
  FileCode: (
    <>
      <path d="M200 224H56a8 8 0 0 1-8-8V40a8 8 0 0 1 8-8h96l56 56v128a8 8 0 0 1-8 8Z" />
      <path d="M152 32v56h56" />
      <path d="m96 144-16 16 16 16M160 144l16 16-16 16" />
    </>
  ),
  FilePdf: (
    <>
      <path d="M48 112V40a8 8 0 0 1 8-8h96l56 56v24" />
      <path d="M152 32v56h56" />
      <path d="M48 152h16a20 20 0 0 1 0 40H48v-40Zm0 40v24" />
      <path d="M112 152h24M124 152v64M168 152h28M168 184h20M168 152v64" />
    </>
  ),
  ShieldCheck: (
    <>
      <path d="M40 56v60c0 84 88 112 88 112s88-28 88-112V56a8 8 0 0 0-8-8H48a8 8 0 0 0-8 8Z" />
      <path d="m96 120 24 24 48-48" />
    </>
  ),
  Clock: (
    <>
      <circle cx="128" cy="128" r="96" />
      <polyline points="128 72 128 128 184 128" />
    </>
  ),
  Megaphone: (
    <>
      <path d="M240 104v48a16 16 0 0 1-16 16h-24l-128 32V56l128 32h24a16 16 0 0 1 16 16Z" />
      <line x1="72" y1="152" x2="72" y2="208" />
    </>
  ),
  Package: (
    <>
      <path d="M40 80l88 48 88-48" />
      <path d="M128 128v96" />
      <path d="M40 176V80l88-48 88 48v96l-88 48Z" />
    </>
  ),
  Handshake: (
    <>
      <path d="M120 84 92 56a16 16 0 0 0-22 0L24 100v56h32l24 24" />
      <path d="m136 84 28-28a16 16 0 0 1 22 0l46 44v56h-32l-56 56-32-32" />
    </>
  ),
  Translate: (
    <>
      <path d="M40 56h112" />
      <path d="M96 56v16c0 53-43 96-96 96" />
      <path d="M56 104c0 35 43 64 96 64" />
      <path d="M136 224l40-96 40 96" />
      <line x1="152" y1="184" x2="200" y2="184" />
    </>
  ),
  Eye: (
    <>
      <path d="M128 56C48 56 16 128 16 128s32 72 112 72 112-72 112-72-32-72-112-72Z" />
      <circle cx="128" cy="128" r="32" />
    </>
  ),
  QrCode: (
    <>
      <rect x="48" y="48" width="52" height="52" rx="4" />
      <rect x="156" y="48" width="52" height="52" rx="4" />
      <rect x="48" y="156" width="52" height="52" rx="4" />
      <path d="M156 156h20v20M196 196h.01M196 156h.01M156 196h.01" />
    </>
  ),
  PaperPlaneTilt: (
    <>
      <path d="M210 35 24 96l72 32 32 72 82-165Z" />
      <line x1="96" y1="128" x2="160" y2="64" />
    </>
  ),
  CalendarCheck: (
    <>
      <rect x="40" y="48" width="176" height="160" rx="8" />
      <line x1="40" y1="96" x2="216" y2="96" />
      <line x1="168" y1="32" x2="168" y2="64" />
      <line x1="88" y1="32" x2="88" y2="64" />
      <path d="m104 152 16 16 32-32" />
    </>
  ),
  // Middleware / integration
  PlugsConnected: (
    <>
      <path d="m144 176-40 40M112 80 72 40M80 112 40 72" />
      <path d="M152 56 96 112l48 48 56-56a34 34 0 0 0-48-48Z" />
      <path d="m104 200 56-56-48-48-56 56a34 34 0 0 0 48 48Z" />
    </>
  ),
  GitMerge: (
    <>
      <circle cx="68" cy="64" r="24" />
      <circle cx="68" cy="192" r="24" />
      <circle cx="188" cy="128" r="24" />
      <path d="M68 88v80M68 112a96 96 0 0 0 96 96" />
    </>
  ),
  CodeBlock: (
    <>
      <rect x="32" y="48" width="192" height="160" rx="8" />
      <path d="m96 112-16 16 16 16M160 112l16 16-16 16" />
    </>
  ),
  // Data
  Database: (
    <>
      <ellipse cx="128" cy="60" rx="80" ry="28" />
      <path d="M48 60v68c0 15.5 35.8 28 80 28s80-12.5 80-28V60" />
      <path d="M48 128v68c0 15.5 35.8 28 80 28s80-12.5 80-28v-68" />
    </>
  ),
  ChartLine: (
    <>
      <path d="M40 48v160a8 8 0 0 0 8 8h168" />
      <path d="m72 168 40-48 32 24 56-64" />
    </>
  ),
  ListChecks: (
    <>
      <path d="M128 128h88M128 64h88M128 192h88" />
      <path d="m36 60 12 12 20-24M36 124l12 12 20-24M36 188l12 12 20-24" />
    </>
  ),
  ArrowsLeftRight: (
    <>
      <path d="M136 80h80l-32-32M120 176H40l32 32" />
      <line x1="216" y1="80" x2="40" y2="80" />
      <line x1="40" y1="176" x2="216" y2="176" />
    </>
  ),
  // Books / finance
  Books: (
    <>
      <path d="M128 88a32 32 0 0 1 32-32h56v144h-56a32 32 0 0 0-32 32" />
      <path d="M128 88a32 32 0 0 0-32-32H40v144h56a32 32 0 0 1 32 32" />
      <line x1="128" y1="88" x2="128" y2="232" />
    </>
  ),
  Calculator: (
    <>
      <rect x="48" y="32" width="160" height="192" rx="8" />
      <rect x="80" y="64" width="96" height="32" rx="2" />
      <path d="M88 136h.01M128 136h.01M168 136h.01M88 176h.01M128 176h.01M168 176h.01" />
    </>
  ),
  Percent: (
    <>
      <line x1="200" y1="56" x2="56" y2="200" />
      <circle cx="84" cy="84" r="28" />
      <circle cx="172" cy="172" r="28" />
    </>
  ),
  // People / places
  UsersThree: (
    <>
      <circle cx="128" cy="96" r="40" />
      <path d="M62 168a72 72 0 0 1 132 0" />
      <path d="M32 200a56 56 0 0 1 60-40M224 200a56 56 0 0 0-60-40" />
    </>
  ),
  Globe: (
    <>
      <circle cx="128" cy="128" r="96" />
      <line x1="32" y1="128" x2="224" y2="128" />
      <ellipse cx="128" cy="128" rx="40" ry="96" />
    </>
  ),
  Buildings: (
    <>
      <path d="M24 216h208" />
      <path d="M120 216V56a8 8 0 0 0-8-8H48a8 8 0 0 0-8 8v160" />
      <path d="M216 216V104a8 8 0 0 0-8-8h-88" />
      <path d="M72 88h16M72 128h16M152 136h16M152 176h16" />
    </>
  ),
  MapPin: (
    <>
      <circle cx="128" cy="104" r="32" />
      <path d="M208 104c0 72-80 128-80 128s-80-56-80-128a80 80 0 0 1 160 0Z" />
    </>
  ),
  TruckSimple: (
    <>
      <path d="M16 160V72a8 8 0 0 1 8-8h136v96M160 96h40l24 32v32h-32" />
      <circle cx="84" cy="184" r="24" />
      <circle cx="180" cy="184" r="24" />
      <line x1="108" y1="184" x2="156" y2="184" />
      <line x1="16" y1="160" x2="60" y2="160" />
    </>
  ),
  HandPalm: (
    <>
      <path d="M80 120V56a16 16 0 0 1 32 0v56" />
      <path d="M112 112V40a16 16 0 0 1 32 0v72" />
      <path d="M144 112V56a16 16 0 0 1 32 0v88a72 72 0 0 1-72 72 72 72 0 0 1-62-36l-26-45a16 16 0 0 1 28-16l12 21" />
    </>
  ),
  ClipboardText: (
    <>
      <rect x="40" y="48" width="176" height="176" rx="8" />
      <path d="M88 48a40 40 0 0 1 80 0" />
      <line x1="88" y1="128" x2="168" y2="128" />
      <line x1="88" y1="160" x2="168" y2="160" />
    </>
  ),
  Stack: (
    <>
      <path d="m24 128 104 56 104-56M24 96l104 56 104-56L128 40 24 96Z" />
      <path d="m24 160 104 56 104-56" />
    </>
  ),
};

export function PhosphorIcon({ name, size = 24, className }) {
  const body = PATHS[name] || PATHS.FileText;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 256 256"
      fill="none"
      stroke="currentColor"
      strokeWidth="16"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {body}
    </svg>
  );
}

export default PhosphorIcon;
